export function parseBackendDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const hasTz = /[zZ]$|[+-]\d{2}:\d{2}$/.test(value);
  const iso = hasTz ? value : `${value}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatInTimeZone(value, timeZone, options = {}, locale) {
  const parsed = parseBackendDateTime(value);
  if (!parsed) return "-";
  const formatOptions = { ...options };
  if (timeZone) formatOptions.timeZone = timeZone;
  try {
    return new Intl.DateTimeFormat(locale, formatOptions).format(parsed);
  } catch (err) {
    if (timeZone) {
      const fallbackOptions = { ...formatOptions };
      delete fallbackOptions.timeZone;
      return new Intl.DateTimeFormat(locale, fallbackOptions).format(parsed);
    }
    return new Intl.DateTimeFormat(locale, formatOptions).format(parsed);
  }
}

export function formatDateTimeInTimeZone(value, timeZone) {
  return formatInTimeZone(value, timeZone, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDateInTimeZone(value, timeZone) {
  return formatInTimeZone(
    value,
    timeZone,
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    },
    "en-US"
  );
}

export function formatDateTimeInBusinessTZ(isoString, businessTimeZone) {
  if (!isoString || !businessTimeZone) return "";
  const d = parseBackendDateTime(isoString);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: businessTimeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

export function getTimeZoneOffsetMinutes(date, timeZone) {
  if (!timeZone || typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") {
    return 0;
  }
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const parts = formatter.formatToParts(date);
    const values = {
      year: null,
      month: null,
      day: null,
      hour: null,
      minute: null,
      second: null,
    };
    for (const part of parts) {
      if (Object.prototype.hasOwnProperty.call(values, part.type)) {
        const num = Number(part.value);
        if (!Number.isNaN(num)) {
          values[part.type] = num;
        }
      }
    }
    if (Object.values(values).some((v) => v === null)) return 0;
    const asUtc = Date.UTC(
      values.year,
      values.month - 1,
      values.day,
      values.hour,
      values.minute,
      values.second
    );
    return (asUtc - date.getTime()) / 60000;
  } catch (err) {
    return 0;
  }
}

export function toUtcDateFromParts(dateStr, timeStr, timeZone) {
  if (!dateStr || !timeStr) return null;
  const [yearStr, monthStr, dayStr] = String(dateStr).split("-");
  const [hhStr, mmStr] = String(timeStr).split(":");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hour = Number(hhStr);
  const minute = Number(mmStr);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const offsetMinutes = getTimeZoneOffsetMinutes(utcGuess, timeZone);
  return new Date(utcGuess.getTime() - offsetMinutes * 60000);
}

export function parseLocalDateTimeInTimeZone(value, timeZone) {
  if (!value) return null;
  const normalized = String(value).trim().replace(" ", "T");
  const [datePart, timePart] = normalized.split("T");
  if (!datePart || !timePart) return null;
  const timeBits = timePart.slice(0, 5);
  return toUtcDateFromParts(datePart, timeBits, timeZone);
}
